import { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  ButtonLink,
  FormBox,
  InputRadio,
  InputTextarea,
  ModalDefault,
  Notice,
  PageActions,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml, usePageHeader } from "@components/ow";
import {
  answerFieldId,
  answersPayload,
  isMultiple,
  questionOptions,
  sortQuestions,
  validateAnswers,
} from "@helpers/quizAnswers";
import { FormPage, FormPageSkeleton, FullWidth, focusFirstError } from "../../Common/FormPage";

// Responder um questionário (modelo Formulário do DS OneWork), dentro do AppShell.
// Uma FormBox por pergunta, todas obrigatórias; o envio passa por uma confirmação,
// porque o back aceita uma resposta por pessoa.

const LIST_HREF = "/e/quiz";
const UNAVAILABLE = "Este questionário não está mais disponível.";

const Stack = styled.div`
  && {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  fieldset {
    margin-bottom: 0;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/** O id do rádio é `nome_valor` na lib; o valor de cada opção é a posição dela. */
const radioFocusId = (question) => `${answerFieldId(question)}_0`;

export default function AnswerQuiz() {
  const { id } = useParams();
  const quizService = useService("quiz");
  const quizAnswersService = useService("quizAnswers");
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [values, setValues] = useState({});
  // Os erros aparecem nos campos depois da primeira tentativa e se atualizam a cada mudança
  const [submitted, setSubmitted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  usePageHeader({
    // O Layout escapa o título
    title: quiz ? quiz.title || "Questionário" : "Questionários",
    breadcrumb: [
      { page: "Questionários", route: LIST_HREF },
      ...(quiz ? [{ page: quiz.title || "Questionário", route: `${LIST_HREF}/${id}` }] : []),
    ],
  });

  useEffect(() => {
    fetchQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function fetchQuiz() {
    try {
      const result = await quizService.get(id);
      if (!result?.data) throw new Error(UNAVAILABLE);
      // Desativado: o back recusaria a resposta
      if (result.data.status === "INATIVO") throw new Error(UNAVAILABLE);
      setQuiz({ ...result.data, questions: sortQuestions(result.data.questions) });
    } catch (error) {
      const status = error?.response?.status;
      toast.error(status === 404 || status === 403 || error?.message === UNAVAILABLE ? UNAVAILABLE : error?.response?.data?.msg || "Erro ao carregar o questionário");
      navigate(LIST_HREF);
    }
  }

  const setAnswer = (questionId, value) => setValues((prev) => ({ ...prev, [questionId]: value }));

  const questions = quiz?.questions || [];
  const fieldErrors = submitted ? validateAnswers(questions, values) : {};

  function handleSubmit() {
    setSubmitted(true);
    const errors = validateAnswers(questions, values);
    const invalid = questions.filter((question) => errors[answerFieldId(question)]);
    if (invalid.length) {
      focusFirstError(invalid.map((question) => (isMultiple(question) ? radioFocusId(question) : answerFieldId(question))));
      return;
    }
    setConfirming(true);
  }

  async function send() {
    setSending(true);
    // Em erro, o service já mostra o toast com a mensagem do back e devolve { error: true }
    const result = await quizAnswersService.send(answersPayload(id, questions, values));
    setSending(false);
    if (!result || result.error) {
      setConfirming(false);
      return;
    }
    toast.success("Respostas enviadas. Obrigado por responder!");
    navigate(LIST_HREF);
  }

  if (!quiz) return <FormPageSkeleton sections={[180, 180]} />;

  if (quiz.answered) {
    return (
      <FormPage>
        <Notice
          $variant="blue"
          $title="Você já respondeu este questionário."
          $text="Cada pessoa responde uma vez só."
          $actions={<ButtonLink $text="Ver questionários" $action={() => navigate(LIST_HREF)} $noPadding $noWidth />}
        />
      </FormPage>
    );
  }

  function renderQuestion(question) {
    const fieldId = answerFieldId(question);
    if (isMultiple(question)) {
      const options = questionOptions(question);
      const selected = options.findIndex((option) => option.title === values[question.id]);
      return (
        <Stack>
          <InputRadio
            name={fieldId}
            $isColumn
            $isRequired
            $errors={fieldErrors[fieldId]}
            $options={options.map((option, index) => ({ label: option.title, value: String(index) }))}
            value={selected >= 0 ? String(selected) : ""}
            onChange={(e) => e.target.name === fieldId && setAnswer(question.id, options[Number(e.target.value)]?.title || "")}
          />
          {fieldErrors[fieldId] ? <Text $text={fieldErrors[fieldId]} $variant="caption_medium" $color="E400" /> : null}
        </Stack>
      );
    }
    return (
      <InputTextarea
        $idForm={fieldId}
        $label="Sua resposta"
        $variant="default"
        $isRequired
        $errors={fieldErrors[fieldId]}
        rows={4}
        value={values[question.id] || ""}
        onChange={(e) => setAnswer(question.id, e.target.value)}
      />
    );
  }

  return (
    <>
      <FormPage
        actions={
          <PageActions>
            <ButtonLink $text="Cancelar" $action={() => navigate(LIST_HREF)} />
            <Button $text="Enviar respostas" $variant="primary" $action={handleSubmit} />
          </PageActions>
        }
      >
        {questions.length === 0 ? (
          <Notice $variant="gray" $text="Este questionário ainda não tem perguntas." />
        ) : (
          questions.map((question, index) => (
            <FormBox key={question.id} title={`Pergunta ${index + 1}`}>
              <FullWidth>
                <Text $text={escapeHtml(question.description || "")} $variant="paragraph_small_bold" $color="N900" />
              </FullWidth>
              <FullWidth>{renderQuestion(question)}</FullWidth>
            </FormBox>
          ))
        )}
      </FormPage>
      {confirming ? (
        <ModalDefault
          $showModal
          $setShowModal={() => !sending && setConfirming(false)}
          $noClose={sending}
          $size="medium"
          $title="Enviar respostas?"
          $contentBackground="N0"
          $footer={
            <>
              <Button $text="Cancelar" $variant="outline" $action={() => setConfirming(false)} $isDisabled={sending} $noWidth />
              <Button $text="Enviar respostas" $variant="primary" $action={send} $isLoading={sending} $noWidth />
            </>
          }
        >
          <Body>
            <Text $text={`“${escapeHtml(quiz.title || "Questionário")}”`} $variant="paragraph_small_medium" $color="N900" />
            <Text $text="Depois de enviar, não dá para mudar as respostas." $variant="paragraph_small_medium" $color="N700" />
          </Body>
        </ModalDefault>
      ) : null}
    </>
  );
}
